/// <amd-module name="TFS/Admin/Views/ProjectPermissions/Sources/ProjectPermissonsSource" />
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IGraphRestClient, GraphClientName } from "TFS/Admin/Common/RestClient/Graph";
import { ICoreRestClient, CoreClientName } from "Tfs/Platform/RestClient/Core";
import { TeamProject } from "Tfs/Platform/Contracts/Core";
import { IPermissionLevelRestClient, PermissionLevelClientName } from "TFS/Admin/Common/RestClient/PermissionLevel";
import * as Graph from "VSS/Platform/Contracts/Graph";
import * as PermissionLevel from "TFS/Admin/Common/Contracts/PermissionLevel";

export class ProjectPermissionsSource {
    public getGraphClient(context: IVssComponentContext) {
        return context.pageContext.getRestClient<IGraphRestClient>(GraphClientName);
    }

    public getPermissionLevelClient(context: IVssComponentContext) {
        return context.pageContext.getRestClient<IPermissionLevelRestClient>(PermissionLevelClientName);
    }

    public getCoreClient(context: IVssComponentContext) {
        return context.pageContext.getRestClient<ICoreRestClient>(CoreClientName);
    }

    public getTeamProject(context: IVssComponentContext, projectId: string): Promise<TeamProject> {
        const restClient = this.getCoreClient(context);
        return restClient.getProject(projectId);
    }

    public getDescriptor(context: IVssComponentContext, storageKey: string): Promise<Graph.GraphDescriptorResult> {
        const restClient = this.getGraphClient(context);
        return restClient.getDescriptor(storageKey);
    }

    public getProjectGroupList(context: IVssComponentContext, scopeDescriptor: string): Promise<Graph.PagedGraphGroups> {
        const restClient = this.getGraphClient(context);
        return restClient.listGroups(scopeDescriptor);
    }

    public getProjectAssignmentsList(
        context: IVssComponentContext,
        projectResourceId: string,
        continuationToken?: string
    ): Promise<PermissionLevel.PagedPermissionLevelAssignment> {
        const restClient = this.getPermissionLevelClient(context);
        return restClient.getPermissionLevelAssignmentsByScope(
            projectResourceId,
            PermissionLevel.PermissionLevelDefinitionScope.Project,
            undefined,
            continuationToken
        );
    }

    public getPermissionLevelDefinitionsForProject(context: IVssComponentContext): Promise<PermissionLevel.PermissionLevelDefinition[]> {
        const restClient = this.getPermissionLevelClient(context);
        return restClient.getPermissionLevelDefinitionsByScope(
            PermissionLevel.PermissionLevelDefinitionScope.Project,
            PermissionLevel.PermissionLevelDefinitionType.SystemDefined
        );
    }

    public getPermissionLevelAssignmentsBySubjectForProject(
        context: IVssComponentContext,
        subjectDescriptor: string,
        resourceId: string
    ): Promise<PermissionLevel.PermissionLevelAssignment[]> {
        const restClient = this.getPermissionLevelClient(context);
        return restClient.getPermissionLevelAssignmentsByScopeAndSubject(
            subjectDescriptor,
            resourceId,
            PermissionLevel.PermissionLevelDefinitionScope.Project
        );
    }

    public assignPermissionLevel(
        context: IVssComponentContext,
        subjectDescriptor: string,
        definitionId: string,
        resourceId: string
    ): Promise<PermissionLevel.PermissionLevelAssignment> {
        const restClient = this.getPermissionLevelClient(context);
        return restClient.assignPermissionLevel(subjectDescriptor, definitionId, resourceId);
    }

    public updatePermissionLevelAssignment(
        context: IVssComponentContext,
        subjectDescriptor: string,
        resourceId: string,
        definitionId: string,
        newDefinitionId: string
    ): Promise<PermissionLevel.PermissionLevelAssignment> {
        const restClient = this.getPermissionLevelClient(context);
        return restClient.updatePermissionLevelAssignment(subjectDescriptor, resourceId, definitionId, newDefinitionId);
    }

    public getSubjects(context: IVssComponentContext, lookupKeys: Graph.GraphSubjectLookup): Promise<{ [key: string]: Graph.GraphSubject }> {
        const restClient = this.getGraphClient(context);
        return restClient.lookupSubjects(lookupKeys);
    }

    public createUser(
        context: IVssComponentContext,
        creationContext: Graph.GraphUserCreationContext,
        groupDescriptors?: any[]
    ): Promise<Graph.GraphUser> {
        const restClient = this.getGraphClient(context);
        return restClient.createUser(creationContext, groupDescriptors);
    }

    public createGroup(
        context: IVssComponentContext,
        creationContext: Graph.GraphGroupCreationContext,
        scopeDescriptor?: string,
        groupDescriptors?: any[]
    ): Promise<Graph.GraphGroup> {
        const restClient = this.getGraphClient(context);
        return restClient.createGroup(creationContext, scopeDescriptor, groupDescriptors);
    }
}
